// src/Projects.tsx
// The caller's video projects: list them, start a new one, open one.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isActiveMember } from '@ring/shared/author-trust';
import { MAX_PROJECT_NAME } from '@ring/shared/projects';
import { createProject, listProjects, type ProjectSummary } from './projectsApi';
import { BookSelect } from './BookSelect';
import { formatWhen } from './Jobs';
import './Projects.css';

export function Projects() {
  const { author, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const active = author ? isActiveMember(author.authorStatus) : false;

  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [name, setName]         = useState('');
  const [bookCode, setBookCode] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && (!author || !active)) navigate('/dashboard');
  }, [authLoading, author, active, navigate]);

  useEffect(() => {
    if (!active) return;
    listProjects()
      .then(setProjects)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load your projects.'));
  }, [active]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const project = await createProject({ name, bookCode: bookCode || null });
      navigate(`/dashboard/projects/${project.projectId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn\'t create the project.');
      setCreating(false);
    }
  }

  if (authLoading || !author || !active) return null;

  return (
    <div className="projects-page">
      <div className="projects-head">
        <div>
          <h1>Projects</h1>
          <p className="projects-sub">Each project is one video ad. <Link to="/dashboard/jobs">See your renders</Link>.</p>
        </div>
      </div>

      {/* Book \ name, like the folder the render is saved in. A name is unique within its book. */}
      <form className="projects-create" onSubmit={onCreate}>
        <label className="projects-field">
          Book
          <BookSelect value={bookCode} onChange={setBookCode} />
        </label>
        <span className="book-path-sep" aria-hidden="true">\</span>
        <label className="projects-field projects-field-grow">
          New project
          <input type="text" required maxLength={MAX_PROJECT_NAME} value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Merlin — hunted hook" />
        </label>
        <button className="projects-primary" type="submit" disabled={creating || !name.trim()}>Create</button>
      </form>

      {error && <p className="page-error-text">{error}</p>}

      {projects === null ? (
        !error && <p className="projects-sub">Loading…</p>
      ) : projects.length === 0 ? (
        <p className="projects-empty">No projects yet. Name one above to start.</p>
      ) : (
        <ul className="projects-list">
          {projects.map(p => (
            <li key={p.projectId}>
              <Link className="projects-row" to={`/dashboard/projects/${p.projectId}`}>
                <span className="projects-row-name">{p.bookCode ? `${p.bookCode} \\ ${p.name}` : p.name}</span>
                <span className="projects-row-meta">
                  <span className={`projects-badge projects-badge-${p.status}`}>{p.status === 'ready' ? 'Ready' : 'Draft'}</span>
                  {p.origin === 'claude' && <span>drafted by Claude</span>}
                  <span>edited {formatWhen(p.updatedAt)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
